<template>
  <div>
    <template v-if="createWorkspaceInProgress">
      <div class="h-100 w-100 d-flex align-center justify-center">
        <div class="progress-container d-flex flex-column align-center text-center">
          <v-progress-circular color="primary" indeterminate size="48" class="mb-5 mt-12" />
          <h5 class="headline5 text-center px-6">
            {{ $t("modals.createWorkspace.requestInProgress") }}
          </h5>
        </div>
      </div>
    </template>
    <div
      v-else-if="createWorkspaceCompleted"
      class="h-100 w-100 d-flex align-center justify-center"
    >
      <div class="progress-container d-flex flex-column align-center text-center">
        <v-icon size="80" class="mb-6" icon="$noIssues" />
        <h5 class="headline5 mr-12 ml-12 text-center">
          {{
            $t("modals.createWorkspace.workspaceCreated", {
              workspace: localValue.item.displayName,
            })
          }}
        </h5>
        <div class="body2 w-60 mt-2 mb-6 mr-12 ml-12">
          {{
            adminExistsInOtherHierarchy
              ? $t("modals.createWorkspace.mspAdminExists", {
                  email: localValue.item.adminEmail,
                })
              : $t("modals.createWorkspace.inviteSent", {
                  email: localValue.item.adminEmail,
                })
          }}
        </div>
        <v-btn size="large" rounded color="primary" @click="$emit('dismissed')">
          {{ $t("general.done") }}
        </v-btn>
      </div>
    </div>
    <div v-else-if="createWorkspaceFailed" class="h-100 w-100 d-flex align-center justify-center">
      <div class="progress-container d-flex flex-column align-center">
        <v-icon size="80" class="mb-6" icon="$warning" />
        <h5 class="headline5 mb-6 mr-12 ml-12 text-center">
          {{ $t("modals.createWorkspace.requestFailed") }}
        </h5>
        <v-btn size="large" rounded color="primary" @click="$emit('dismissed')">
          {{ $t("general.ok") }}
        </v-btn>
      </div>
    </div>
    <v-form ref="form" v-else v-model="valid" @submit.prevent>
      <v-stepper v-model="currentStep" width="100vw">
        <template v-slot:default>
          <div class="d-flex justify-center w-100 scroll-area">
            <div class="dialog-content">
              <v-stepper-header>
                <div class="mt-6 progress-bar__content">
                  <div class="body3 mb-4">
                    {{ $t("general.step") }}
                    {{ getCurrentStepNumber(steps) }}
                    {{ $t("general.of") }}
                    {{ steps.length }}
                  </div>
                  <v-progress-linear color="#46A762" v-model="stepsProgress"></v-progress-linear>
                </div>
                <template v-for="n in steps" :key="`${n}-step`">
                  <v-stepper-item
                    class="d-none"
                    :complete="currentStep > n"
                    :step="`Step {{ n }}`"
                    :value="n"
                    editable
                  ></v-stepper-item>
                </template>
              </v-stepper-header>
              <v-stepper-window :key="currentStep">
                <v-stepper-window-item v-for="step in steps" :key="`${step}-content`" :value="step">
                  <div class="stepper-step__wrapper">
                    <div>
                      <msp-workspace-company-details-step
                        v-if="step === workspaceCreationSteps.COMPANY_DETAILS"
                        :workspace="localValue.item"
                        :show-parent-workspace-input="showParentWorkspaceIdInput"
                        :parent-workspaces="parentWorkspaces.items"
                        :parent-workspaces-loading="parentWorkspaceInputLoading"
                        :parent-workspace-id-rules="parentWorkspaceIdRules"
                        :show-limits="isChildWorkspace || isRegularWorkspace"
                        @parent-workspace-search="onParentWorkspaceSearchUpdate"
                        @parent-workspace-intersect="onParentWorkspaceIntersect"
                      />
                      <msp-workspace-settings-step
                        v-if="step === workspaceCreationSteps.MSP_SETTINGS"
                        v-model:workspace="localValue.item"
                        :show-labels-field="showLabelsField"
                        :label-options="labels"
                        :show-global-policy="isChildWorkspace"
                        :global-policies="globalPolicies.items"
                        :global-policies-loading="globalPoliciesLoading"
                        :show-billing-fields="isGlobalContext"
                        :has-parent-workspace="hasParentWorkspace"
                        :notification-labels="notificationLabels"
                        :advanced-setting-switches="advancedSettingSwitches"
                        @label-search="onLabelSearch"
                        @global-policy-intersect="onWorkspacePolicyIntersect"
                      />
                      <msp-workspace-branding-step
                        v-if="step === workspaceCreationSteps.BRANDING"
                        :workspace="localValue.item"
                        :alias-disabled="!isGlobalAdmin"
                      />
                      <template v-if="step === workspaceCreationSteps.SUBSCRIPTION_RULES">
                        <div class="headline5 mb-3">
                          {{ $t("modals.createWorkspace.subscriptionRules") }}
                        </div>
                        <cui-subscription-rules-card
                          class="py-6"
                          :hidden-modules="hiddenModules"
                          prune-hidden-modules
                          v-model:subscription-config="localValue.item.subscriptionRules"
                          :bundle-or-module-is-required-message="
                            $t('validations.bundleOrModuleIsRequired')
                          "
                          :validation-aria-label="$t('ariaLabels.subscriptionRulesValidation')"
                          :allowed-bundles-label="$t('subscriptions.allowedBundles')"
                          :choose-available-bundles-label="
                            $t('subscriptions.chooseAvailableBundles')
                          "
                          :new-bundles-label="$t('subscriptions.newBundles')"
                          :legacy-bundles-label="$t('subscriptions.legacyBundles')"
                          :allowed-modules-label="$t('subscriptions.allowedModules')"
                          :choose-allowed-modules-label="$t('subscriptions.chooseAllowedModules')"
                          v-bind="subscriptionRulesNames"
                          :please-select-at-least-one-option-message="
                            $t('validations.bundleOrModuleIsRequired')
                          "
                          :parent-workspace-subscription-config="parentSubscriptionRules"
                        />
                      </template>

                      <template v-if="step === workspaceCreationSteps.SUBSCRIPTION">
                        <div class="headline5 mb-3">
                          {{ $t("modals.createWorkspace.chooseYourSubscription") }}
                        </div>
                        <cui-subscription-editor
                          v-model="localValue.item.subscription"
                          :rules="localValue.item.subscriptionRules"
                          :selection-mode="CuiBundleSelectionMode.SINGLE"
                          :hidden-modules="hiddenModules"
                          :show-required-error="valid === false"
                          :labels="subscriptionEditorLabels"
                          v-bind="subscriptionEditorNames"
                          test-id-prefix="create-workspace-modal-subscription"
                        >
                          <template #addon-disabled="{ moduleLabel }">
                            <span v-html="addonDisabledHint(moduleLabel)"></span>
                          </template>
                        </cui-subscription-editor>
                      </template>

                      <template v-if="step === workspaceCreationSteps.REVIEW_SUBSCRIPTION">
                        <div class="headline5 mb-3">
                          {{ $t("modals.createWorkspace.reviewSubscription") }}
                        </div>
                        <div class="d-flex w-100">
                          <cui-subscription-review
                            :model-value="localValue.item.subscription"
                            :labels="subscriptionReviewLabels"
                            v-bind="subscriptionReviewNames"
                            :hidden-modules="hiddenModules"
                            test-id-prefix="create-workspace-modal-review"
                          >
                            <template #bundle-module="{ module, moduleInfo }">
                              {{ $t(`subscriptions.modules.${module}`) }}
                              <span v-if="hasSocAddonInModule(moduleInfo)">
                                - {{ $t("moduleOrAddonSelector.managed") }}
                              </span>
                            </template>
                            <template #module-subtitle="{ moduleInfo }">
                              {{
                                hasSocAddonInModule(moduleInfo)
                                  ? $t("modals.createWorkspace.managed")
                                  : $t("modals.createWorkspace.unmanaged")
                              }}
                            </template>
                          </cui-subscription-review>
                        </div>
                      </template>
                    </div>
                  </div>
                </v-stepper-window-item>
              </v-stepper-window>
            </div>
          </div>
          <v-divider class="mb-4"></v-divider>
        </template>
        <template v-slot:actions="{ prev, next }">
          <div class="d-flex align-center justify-center w-100">
            <div class="d-flex justify-end align-center pt-1 dialog-content">
              <div>
                <v-btn
                  variant="text"
                  color="primary"
                  size="large"
                  @click="onPrevStepClick(prev, currentStep)"
                  v-if="showPreviousStepBtn"
                >
                  {{ $t("general.back") }}
                </v-btn>
                <v-btn
                  rounded
                  v-if="!showCompleteBtn"
                  color="primary"
                  size="large"
                  class="ml-2"
                  :disabled="disable"
                  @click="onNextStepClick(next)"
                >
                  {{ $t("general.next") }}
                </v-btn>
                <v-btn
                  rounded
                  v-else
                  color="primary"
                  size="large"
                  class="ml-2"
                  :disabled="disable"
                  :loading="createWorkspaceClicked"
                  @click="createWorkspace()"
                >
                  {{ $t(`modals.${config.action}.actionBtn`) }}
                </v-btn>
              </div>
            </div>
          </div>
        </template>
      </v-stepper>
    </v-form>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, onMounted, type PropType, ref, watch } from "vue";
import { useMspWorkspaceModal, WorkspaceCreationSteps } from "@/composables/useMspWorkspaceModal";
import { useWorkspaceFormText } from "@/composables/useWorkspaceFormText";
import { useInfiniteScroll } from "@/composables/useInfiniteScroll";
import cloneDeep from "lodash/cloneDeep";
import {
  type MspWorkspaceDialogItem,
  NotificationsLevel,
  type ParentWorkspaceSearchPermission,
  type SubscriptionRulesConfig,
  useMspStore,
} from "@/_store/msp/msp.module.ts";
import {
  MspWorkspaceAction,
  SubscriptionAddon,
  SubscriptionBundle,
  WorkspaceLocale,
  WorkspaceType,
} from "@/constants/workplaces";
import type { DialogDataConfig } from "@/_store/dialogs.module";
import { storeToRefs } from "pinia";
import { maxItems, required } from "@/_helpers/validators";
import debounce from "lodash/debounce";
import { isGlobalActionRestricted } from "@/_helpers/global-permissions";
import { type MspLabel, useMspLabelsStore } from "@/_store/msp/msp-labels.module.ts";
import uniqBy from "lodash/uniqBy";
import type { Paged } from "@/types.ts";
import {
  CuiBundleSelectionMode,
  CuiSubscriptionEditor,
  CuiSubscriptionReview,
  createEmptySubscription,
  getDefaultSubscriptionRules,
  getHiddenSubscriptionModules,
  getNotificationLevels,
  handleVirtualScrollData,
  hasSocAddonInModule,
} from "@coronet/cui";
import { useGlobalWorkspacesStore } from "@/_store/global-workspaces.module.ts";
import { usePermissionsStore } from "@/_store/permissions.module.ts";
import { useWorkspaceStore } from "@/_store/workspace.module.ts";
import {
  type GlobalPolicyListItem,
  useMspGlobalPoliciesStore,
} from "@/_store/msp/global-policies/msp-global-policies.module.ts";
import { ManageWorkspacesContext } from "@/constants/msp.ts";
import { CuiSubscriptionRulesCard, WorkspaceAdvancedSetting } from "@coronet/cui";
import { isCanadaEnv } from "@/constants/general.ts";
import MspWorkspaceCompanyDetailsStep from "@/components/modals/msp/create-workspace/MspWorkspaceCompanyDetailsStep.vue";
import MspWorkspaceSettingsStep from "@/components/modals/msp/create-workspace/MspWorkspaceSettingsStep.vue";
import MspWorkspaceBrandingStep from "@/components/modals/msp/create-workspace/MspWorkspaceBrandingStep.vue";
import { MspPortalScopeSection, MspRolePermissionScopes } from "@/constants/roles.ts";

/** The create dialog always sets its subscription rules: the defaults, or the parent's rules. */
type CreateMspWorkspaceDialogItem = MspWorkspaceDialogItem & {
  subscriptionRules: SubscriptionRulesConfig;
};

export default defineComponent({
  props: {
    config: {
      type: Object as PropType<DialogDataConfig<MspWorkspaceDialogItem>>,
      required: true,
    },
    disable: {
      type: Boolean,
      required: true,
    },
  },
  components: {
    CuiSubscriptionRulesCard,
    CuiSubscriptionEditor,
    CuiSubscriptionReview,
    MspWorkspaceCompanyDetailsStep,
    MspWorkspaceSettingsStep,
    MspWorkspaceBrandingStep,
  },
  emits: ["dismissed", "update:localValue"],
  setup(props, { emit }) {
    const parentWorkspaceInputLoading = ref(false);
    const labelsStore = useMspLabelsStore();
    const parentWorkspaces = ref<Paged<{ workspaceName: string; workspaceId: string }>>({
      items: [],
      total: 0,
    });
    const parentWorkspaceSearchPage = ref(0);
    const parentWorkspaceSearchValue = ref("");
    const hasMoreWorkspaces = ref(true);
    const globalPolicies = ref<Paged<GlobalPolicyListItem>>({
      items: [],
      total: 0,
    });
    const globalPoliciesLoading = ref(false);
    const globalPoliciesSearchPage = ref(0);
    const hasMorePolicies = ref(true);
    const valid = ref(true);
    const workspaceStore = useWorkspaceStore();
    const permissionsStore = usePermissionsStore();
    const mspGlobalPoliciesStore = useMspGlobalPoliciesStore();
    const { isGlobalAdmin } = storeToRefs(permissionsStore);
    const adminExistsInOtherHierarchy = ref(false);
    const labels = ref<MspLabel[]>([] as MspLabel[]);
    const mspStore =
      props.config.item.context === ManageWorkspacesContext.GLOBAL_WORKSPACES
        ? useGlobalWorkspacesStore()
        : useMspStore();
    const { createWorkspaceInProgress, createWorkspaceFailed, createWorkspaceCompleted } =
      storeToRefs(mspStore);
    const createWorkspaceClicked = ref(false);
    const form = ref();
    const parentSubscriptionRules = ref<SubscriptionRulesConfig | undefined>(undefined);
    const type = computed(() => {
      switch (props.config.action) {
        case MspWorkspaceAction.CREATE_REGULAR_WORKSPACE:
          return WorkspaceType.REGULAR;
        case MspWorkspaceAction.CREATE_CHILD_WORKSPACE:
          return WorkspaceType.CHILD;
        case MspWorkspaceAction.CREATE_CHANNEL_WORKSPACE:
          return WorkspaceType.CHANNEL;
        default:
          return null;
      }
    });

    const showSubscriptionRulesStep = computed(() => {
      const isChannel = type.value === WorkspaceType.CHANNEL;
      const isRegular = type.value === WorkspaceType.REGULAR;
      if (props.config.item.context === ManageWorkspacesContext.GLOBAL_WORKSPACES) {
        return isChannel || isRegular;
      }
      return isChannel;
    });

    const localValue = ref<DialogDataConfig<CreateMspWorkspaceDialogItem>>(
      cloneDeep({
        ...props.config,
        item: {
          companyName: null,
          displayName: null,
          domain: null,
          adminEmail: null,
          notificationsLevel: NotificationsLevel.ALL,
          type: type.value,
          supportEnabled: true,
          scanUnprotectedUsers: true,
          psaEnabled: true,
          showDisabledModules: true,
          isCoroEmployeeWorkspace: false,
          languageCode: WorkspaceLocale.EN_US,
          branding: {},
          subscription: createEmptySubscription(),
          subscriptionRules: getDefaultSubscriptionRules(type.value),
          labels: [],
          limited: false,
        },
      }) as unknown as DialogDataConfig<CreateMspWorkspaceDialogItem>
    );

    const {
      currentStep,
      showPreviousStepBtn,
      showCompleteBtn,
      getCurrentStepNumber,
      getStepsProgress,
    } = useMspWorkspaceModal();
    const {
      subscriptionEditorLabels,
      subscriptionEditorNames,
      subscriptionReviewLabels,
      subscriptionReviewNames,
      subscriptionRulesNames,
      addonDisabledHint,
      notificationLevelLabels,
    } = useWorkspaceFormText();

    const showLabelsField = computed(() => {
      return (
        props.config.item.context === ManageWorkspacesContext.SINGLE_HIERARCHY &&
        localValue.value.item.type === WorkspaceType.CHILD
      );
    });

    const steps = computed(() => {
      const allSteps = [
        WorkspaceCreationSteps.COMPANY_DETAILS,
        WorkspaceCreationSteps.MSP_SETTINGS,
        WorkspaceCreationSteps.SUBSCRIPTION,
        WorkspaceCreationSteps.REVIEW_SUBSCRIPTION,
      ];

      if (isChannelWorkspace.value) {
        const mspIndex = allSteps.indexOf(WorkspaceCreationSteps.MSP_SETTINGS);
        if (mspIndex !== -1) {
          allSteps.splice(mspIndex + 1, 0, WorkspaceCreationSteps.BRANDING);
        }
      }

      if (showSubscriptionRulesStep.value) {
        const subscriptionIndex = allSteps.indexOf(WorkspaceCreationSteps.SUBSCRIPTION);
        if (subscriptionIndex !== -1) {
          allSteps.splice(subscriptionIndex, 0, WorkspaceCreationSteps.SUBSCRIPTION_RULES);
        }
      }

      return allSteps;
    });

    const showParentWorkspaceIdInput = computed(() => {
      return type.value !== WorkspaceType.REGULAR;
    });

    const isChannelWorkspace = computed(
      () => props.config.action === MspWorkspaceAction.CREATE_CHANNEL_WORKSPACE
    );

    const isChildWorkspace = computed(
      () => props.config.action === MspWorkspaceAction.CREATE_CHILD_WORKSPACE
    );

    const isRegularWorkspace = computed(() => !isChannelWorkspace.value && !isChildWorkspace.value);

    const hiddenModules = computed(() =>
      getHiddenSubscriptionModules([localValue.value.item.type], { hideSat: isCanadaEnv })
    );

    const isGlobalContext = computed(
      () =>
        isGlobalAdmin.value &&
        props.config.item.context === ManageWorkspacesContext.GLOBAL_WORKSPACES
    );

    const domainDisplayName = computed(() => {
      return isGlobalAdmin.value ? "coro.net" : workspaceStore.workspace.domain;
    });

    const stepsProgress = computed(() => {
      return getStepsProgress(steps.value);
    });

    const showScanUnprotectedUsers = computed(() => {
      if (type.value === WorkspaceType.CHILD) {
        return true;
      }
      return isChannelWorkspace.value;
    });

    const hasParentWorkspace = computed(() => !!localValue.value.item.parentWorkspaceId);

    const notificationLabels = computed(() =>
      notificationLevelLabels({
        workspaceType: localValue.value.item.type,
        hasParent: hasParentWorkspace.value,
        domain: domainDisplayName.value,
      })
    );

    const advancedSettingSwitches = computed(() => [
      ...(isChannelWorkspace.value ? [WorkspaceAdvancedSetting.SUPPORT_ENABLED] : []),
      ...(showScanUnprotectedUsers.value ? [WorkspaceAdvancedSetting.SCAN_UNPROTECTED_USERS] : []),
      WorkspaceAdvancedSetting.SHOW_DISABLED_MODULES,
      ...(isGlobalAdmin.value ? [WorkspaceAdvancedSetting.CORO_EMPLOYEE_WORKSPACE] : []),
    ]);

    const parentWorkspaceIdRules = computed(() => {
      const isChild = type.value === WorkspaceType.CHILD;
      const isChannel = type.value === WorkspaceType.CHANNEL;

      // If not a Child or Channel, no validation is required.
      if (!isChild && !isChannel) {
        return [];
      }

      // For Child workspaces - parentWorkspaceId is always required.
      if (isChild) {
        return [required()];
      }

      // Handling Channel logic
      if (isGlobalContext.value) {
        const isRestricted = isGlobalActionRestricted(
          MspRolePermissionScopes.MSP_PORTAL_SCOPE,
          MspPortalScopeSection.MSP_WORKSPACES,
          "createChannelWorkspace"
        );
        // If restricted (cannot create root), parent ID is required.
        // If not restricted (can create root), parent ID is optional.
        return isRestricted ? [required()] : [];
      }

      // Default Channel behavior: parentWorkspaceId is always required
      return [required()];
    });

    const onNextStepClick = async (callback: () => void) => {
      const validationResult = await form.value?.validate();
      if (validationResult?.valid) {
        callback();
      }
    };

    /** Clears the chosen subscription, which was picked against rules that are about to change. */
    const resetSubscription = () => {
      localValue.value.item.subscription = createEmptySubscription();
    };

    const onPrevStepClick = async (callback: () => void, currentStep: WorkspaceCreationSteps) => {
      await form.value?.resetValidation();
      // If on subscription we're returning to Subscription Rules, we should reset the selected state
      // of the subscription as the allowed modules and bundles can be changed
      if (showSubscriptionRulesStep.value && currentStep === WorkspaceCreationSteps.SUBSCRIPTION) {
        resetSubscription();
      }
      callback();
    };

    const getLabels = async (search: string = "") => {
      try {
        const { data } = await labelsStore.searchLabels(
          search,
          localValue.value.item.parentWorkspaceId
        );
        labels.value = uniqBy([...localValue.value.item.labels, ...data], "id");
      } catch (e) {
        console.error(e);
        labels.value = [];
      }
    };

    const onLabelSearch = debounce(async (search: string) => {
      await getLabels(search);
    }, 500);

    const onParentWorkspaceSearchUpdate = debounce(function (searchString) {
      parentWorkspaceSearchPage.value = 0;
      parentWorkspaceSearchValue.value = searchString ?? "";
      if (!searchString && !localValue.value.item.parentWorkspaceId) {
        fetchParentWorkspaces();
      }
      if (searchString !== localValue.value.item.parentWorkspaceId) {
        fetchParentWorkspaces(searchString);
      }
    }, 500);

    const fetchParentWorkspaces = async (search: string = "", virtualScroll = false) => {
      if (props.disable) return;
      const permission: ParentWorkspaceSearchPermission =
        props.config.action === MspWorkspaceAction.CREATE_CHANNEL_WORKSPACE
          ? "CREATE_CHANNEL_WORKSPACE"
          : "CREATE_CHILD_WORKSPACE";
      parentWorkspaceInputLoading.value = true;
      const candidates = await mspStore.getWorkspaceCandidates(search, permission, {
        page: parentWorkspaceSearchPage.value,
        pageSize: 25,
      });
      hasMoreWorkspaces.value = parentWorkspaces.value.items.length < candidates.total;
      parentWorkspaces.value = virtualScroll
        ? {
            items: handleVirtualScrollData(
              parentWorkspaces.value.items,
              candidates.items,
              "workspaceId"
            ),
            total: candidates.total,
          }
        : candidates;
      parentWorkspaceInputLoading.value = false;
    };

    const fetchGlobalPolicies = async () => {
      if (props.disable) return;
      globalPoliciesLoading.value = true;

      const { data } = await mspGlobalPoliciesStore.searchGlobalPolicies("", {
        page: globalPoliciesSearchPage.value,
        pageSize: 25,
      });
      hasMorePolicies.value = globalPolicies.value.items.length < data.total;
      globalPolicies.value = {
        items: handleVirtualScrollData(globalPolicies.value.items, data.items, "id"),
        total: data.total,
      };
      globalPoliciesLoading.value = false;
    };

    const createWorkspace = async () => {
      // create workspace clicked is here to prevent double-click on create workspace
      // createWorkspaceInProgress can not be used because user can click on button twice
      // while he/she is waiting for response from checkInvitedAdminAlreadyMspAdmin
      createWorkspaceClicked.value = true;
      if (showParentWorkspaceIdInput.value && localValue.value.item.parentWorkspaceId) {
        adminExistsInOtherHierarchy.value = await mspStore.checkInvitedAdminAlreadyMspAdmin({
          parentWorkspaceId: localValue.value.item.parentWorkspaceId!,
          invitedAdmin: localValue.value.item.adminEmail!,
        });
      }
      try {
        const data = localValue.value;
        const createWorkspaceCallback = props.config.callback ?? mspStore.createWorkspace;
        await createWorkspaceCallback(data);
      } catch (e) {
        console.error(e);
      } finally {
        createWorkspaceClicked.value = false;
      }
    };

    const { onIntersect: onParentWorkspaceIntersect } = useInfiniteScroll(
      computed(() => parentWorkspaces.value.items),
      { hasMore: hasMoreWorkspaces },
      () => {
        parentWorkspaceSearchPage.value += 1;
        return fetchParentWorkspaces(parentWorkspaceSearchValue.value, true);
      }
    );

    const { onIntersect: onWorkspacePolicyIntersect } = useInfiniteScroll(
      computed(() => globalPolicies.value.items),
      { hasMore: hasMorePolicies },
      () => {
        globalPoliciesSearchPage.value += 1;
        return fetchGlobalPolicies();
      }
    );

    watch(
      () => localValue.value?.item.parentWorkspaceId,
      async (value) => {
        labels.value = [];
        getLabels();
        // Local administrators need a parent: once it is cleared, that level falls back to the
        // default, or Create would send a level the backend rejects.
        const levels = getNotificationLevels(localValue.value.item.type, { hasParent: !!value });
        if (!levels.includes(localValue.value.item.notificationsLevel)) {
          localValue.value.item.notificationsLevel = NotificationsLevel.ALL;
        }
        // The parent's rules decide what can be bought, so a subscription chosen for another
        // parent may no longer be allowed.
        resetSubscription();
        if (value) {
          const workspaceSubscriptionRules =
            await useMspStore().getWorkspaceSubscriptionRules(value);
          parentSubscriptionRules.value = structuredClone(workspaceSubscriptionRules);
          localValue.value.item.subscriptionRules = structuredClone(workspaceSubscriptionRules);
        } else {
          parentSubscriptionRules.value = undefined;
          // A new object on every call: the rules card changes the config it is handed.
          localValue.value.item.subscriptionRules = getDefaultSubscriptionRules(type.value);
        }
      }
    );

    watch(
      localValue,
      (value) => {
        emit("update:localValue", value);
      },
      { deep: true }
    );

    onMounted(async () => {
      mspStore.resetCreateWorkspaceLoaders();
      if (showLabelsField.value) {
        await getLabels();
      }
      if (showParentWorkspaceIdInput.value) {
        await fetchParentWorkspaces();
      }
      if (isChildWorkspace.value) {
        await fetchGlobalPolicies();
      }
    });

    return {
      globalPolicies,
      createWorkspaceInProgress,
      createWorkspaceClicked,
      createWorkspaceFailed,
      createWorkspaceCompleted,
      localValue,
      form,
      valid,
      adminExistsInOtherHierarchy,
      isChannelWorkspace,
      isChildWorkspace,
      isRegularWorkspace,
      hiddenModules,
      isGlobalContext,
      hasParentWorkspace,
      notificationLabels,
      advancedSettingSwitches,
      onParentWorkspaceSearchUpdate,
      parentWorkspaceInputLoading,
      parentWorkspaces,
      fetchParentWorkspaces,
      showParentWorkspaceIdInput,
      maxItems,
      currentStep,
      steps,
      workspaceCreationSteps: WorkspaceCreationSteps,
      bundles: SubscriptionBundle,
      showPreviousStepBtn,
      showCompleteBtn,
      getCurrentStepNumber,
      stepsProgress,
      onNextStepClick,
      onPrevStepClick,
      hasSocAddonInModule,
      createWorkspace,
      subscriptionAddons: SubscriptionAddon,
      isGlobalAdmin,
      parentWorkspaceIdRules,
      labels,
      onLabelSearch,
      showLabelsField,
      onParentWorkspaceIntersect,
      onWorkspacePolicyIntersect,
      globalPoliciesLoading,
      parentSubscriptionRules,
      subscriptionRulesNames,
      CuiBundleSelectionMode,
      subscriptionEditorLabels,
      subscriptionEditorNames,
      subscriptionReviewLabels,
      subscriptionReviewNames,
      addonDisabledHint,
    };
  },
});
</script>

<style lang="scss" scoped>
.progress-container {
  height: 300px;
}

.v-stepper.v-sheet {
  box-shadow: unset !important;
  background-color: transparent;
}

.v-stepper-header {
  box-shadow: unset !important;
}

.v-stepper-window {
  margin: unset !important;
}

.stepper-step__wrapper {
  padding-bottom: 20px;
  padding-top: 20px;

  .v-card {
    border-radius: 8px !important;
  }
}

.dialog-content {
  min-width: 650px;
  max-width: 1150px;
  width: 100%;
  margin-right: 30px;
  margin-left: 30px;
}

.scroll-area {
  height: calc(100vh - 160px);
  overflow: auto;
}

:deep(*) {
  .v-chip__close {
    .icon-rules-close:before {
      color: rgb(var(--v-theme-primary));
    }
  }
}

.progress-bar__content {
  width: 200px;
}
</style>
